import { getLogger } from '@confluence/logger';
import { fg } from '@confluence/feature-gating';

const logger = getLogger('scroll-to-element');

/**
 * Returns the HTMLElement that should be used as the scroll root.
 * This is the div with id=AkMainContent when on desktop, falling back to window on mobile.
 */
function getScrollRootElement(): HTMLElement | Window | null {
	// On mobile sizes, scroll the window instead of #AkMainContent.
	// The magic number maps to media.above.md @media (min-width: 64rem) = 1024px.
	if (typeof document !== 'undefined' && typeof window !== 'undefined') {
		const akMainContent = document.getElementById('AkMainContent');
		if (window.innerWidth > 1023 && akMainContent) {
			return akMainContent;
		}
		return window;
	}
	return null;
}
const explicitScrollListeners = new Set<() => void>();
export function addExplicitScrollListener(listener: () => void): () => void {
	explicitScrollListeners.add(listener);

	return () => explicitScrollListeners.delete(listener);
}

function invokeExplicitScrollListeners() {
	explicitScrollListeners.forEach((listener) => {
		try {
			listener();
		} catch (error) {
			Promise.all([
				import(
					/* webpackChunkName: "loadable-confluencemonitoring" */ '@confluence/monitoring/entry-points/getMonitoringClient.ts'
				),
				import(
					/* webpackChunkName: "loadable-confluenceerror-boundary" */ '@confluence/error-boundary/entry-points/Attribution.ts'
				),
			])
				.then(([{ getMonitoringClient }, { Attribution }]) => {
					getMonitoringClient().submitError(error, {
						attribution: Attribution.PAGE_EXTENSIONS,
					});
				})
				.catch(() => {
					// ignore errors, we can't do anything at this point
				});
		}
	});
}

function isSmoothScrollSupported() {
	return typeof document !== 'undefined' && 'scrollBehavior' in document.documentElement.style;
}

export function scrollToPageStart(isSmooth: boolean = false): void {
	invokeExplicitScrollListeners();

	const scrollRootElement = getScrollRootElement();
	if (scrollRootElement) {
		// scrollRootElement is null during SSR — skip scrolling in that case
		if (isSmooth && isSmoothScrollSupported()) {
			scrollRootElement.scrollTo({
				top: 0,
				left: 0,
				behavior: 'smooth',
			});
		} else {
			scrollRootElement.scrollTo(0, 0);
		}
	}
}

/**
 * Scrolls element into browser view, taking various offsets into account.
 *
 * @param element - the event which you want to scroll into view
 * @param scrollCorrectionPixels - the amount of pixels the scroll position needs to be adjusted for. For example,
 * if you have a fixed-positioned element that overlaps your scroll area,
 * and that element has a height of `60` pixels, you'll set `scrollCorrectionPixels` to `-60`.
 */
export function scrollToElement(
	element: HTMLElement,
	scrollCorrectionPixels?: number,
	scrollBehavior: ScrollBehavior = 'auto',
	isLivePage?: boolean,
): void {
	const scrollRootElement = isLivePage
		? typeof document !== 'undefined'
			? (document.querySelector('.fabric-editor-popup-scroll-parent') as HTMLElement)
			: null
		: getScrollRootElement();

	let parentsOffset: number = 0;
	let currentOffsetParent = element.offsetParent;
	while (currentOffsetParent instanceof HTMLElement && currentOffsetParent != scrollRootElement) {
		parentsOffset += currentOffsetParent.offsetTop;
		currentOffsetParent = currentOffsetParent.offsetParent;
	}

	const pageHeaderHeightAdjustment = fg('confluence_frontend_content_wrapper') ? 56 : 0;

	let top =
		element.offsetTop + (scrollCorrectionPixels || 0) + parentsOffset - pageHeaderHeightAdjustment;

	// check if element is nested inside table, if so we need to account for
	// sticky table header overlapping the element
	const hasTableParent = element?.closest('.pm-table-container td');
	if (hasTableParent && scrollCorrectionPixels) top = top + scrollCorrectionPixels * 1.25;

	invokeExplicitScrollListeners();

	let behavior = scrollBehavior;
	if (behavior === 'smooth' && !isSmoothScrollSupported()) {
		behavior = 'auto';
	}

	try {
		scrollRootElement?.scroll({
			top,
			behavior,
		});
	} catch {
		logger.error`scroll() is not supported on #AkMainContent`;
	}
}

/**
 * Scrolls an element to the top of the view for block link (#block-xxx) navigation.
 * Uses AkMainContent as scroll container when the element is inside it (Confluence view page).
 * Falls back to scrollIntoView when AkMainContent is not the scroll container.
 */
export function scrollToBlockForViewPage(element: HTMLElement): void {
	if (typeof document !== 'undefined') {
		const scrollContainer = document.getElementById('AkMainContent');
		const breadcrumbHeaderHeight = 56; // FixedSpaceHeaderContainer (top nav) of the classic full page
		if (scrollContainer && scrollContainer.contains(element)) {
			// Let the browser calculate the exact scroll position
			const savedScroll = scrollContainer.scrollTop;
			element.scrollIntoView({ behavior: 'instant', block: 'start' });
			const targetScroll = scrollContainer.scrollTop;
			scrollContainer.scrollTop = savedScroll;
			scrollContainer.scrollTo({
				left: 0,
				top: targetScroll - breadcrumbHeaderHeight,
				behavior: 'smooth',
			});
		} else {
			element.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}
}
